import { useCallback } from 'react';
import DeviceInfo from '../../../DeviceInfo';
import { isIOS } from '../../../isIOS';
import { isNotNil } from '../../../isNotNil';
import { useOpenDeeplink } from '../../../../hooks';
import {
  appendParams,
  finalizeUrl,
  getDefaultWebviewLink,
  getWebviewOrigin,
} from '../../utils';
import { useRootStackNavigation } from '../../../../hooks/useRootNavigation';
import { useWebRoutePush } from '../../../../hooks/useWebRoutePush';
import { useExperimentGroup } from '../../../../context/UserContext';
import { isWeb } from '../../../isWeb';
import { toAbsoluteWebUrl } from '../../../resolveWebviewDeeplinkUrl';
import { isEmbeddedWebviewTarget } from '../../../../webRoutePaths';

const getPath = (path: keyof typeof WEBVIEW_PAGE | string) => {
  if (isNotNil(WEBVIEW_PAGE[path as keyof typeof WEBVIEW_PAGE])) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return WEBVIEW_PAGE[path];
  }
  return path;
};

export const useMoveToWebview = () => {
  const { openDeeplink } = useOpenDeeplink();
  const navigation = useRootStackNavigation();
  const { pushWebviewWrappedScreen } = useWebRoutePush();

  const isMainRN = useExperimentGroup({
    sno: 'EXP1795',
    targetGroups: ['B'],
  });

  return useCallback(
    async (
      path: keyof typeof WEBVIEW_PAGE | string,
      { titleBar = false }: MoveToWebviewOptions = {},
    ) => {
      const webviewPath = getPath(path);

      // web: 기본은 실제 웹을 새 탭으로 열고, 앱 안에 남아야 하는 경로만 RN 화면으로 보낸다.
      // 규칙을 useOpenDeeplink 의 ably://webview 처리와 동일하게 맞춘다.
      //
      // ⚠️ 아래 await 보다 먼저 동기적으로 판단해야 한다. await 를 거치면 브라우저
      // user activation 이 끊겨 window.open 이 팝업 차단에 걸린다.
      // (toAbsoluteWebUrl / pushWebviewWrappedScreen / isEmbeddedWebviewTarget 모두 sync)
      if (isWeb) {
        const webUrl = toAbsoluteWebUrl(webviewPath);

        // 전용 화면이 있는 경로면 그쪽으로(Cart 등). 네이티브 분기와 같은 규칙.
        if (pushWebviewWrappedScreen(webUrl)) return;

        // 스택 안에 남아야 하는 경로는 새 탭 대신 임베드.
        if (isEmbeddedWebviewTarget(webUrl)) {
          navigation.push('WebViewScreen', { url: webUrl });
          return;
        }

        window.open(webUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const url = getDefaultWebviewLink(titleBar);
      const resultUrl = `${await getWebviewOrigin()}${webviewPath}`;

      if (isMainRN) {
        // 전용 화면이 있는 경로면 그쪽으로. 범용 WebViewScreen 으로 가면 목적지가 url params
        // 문자열로 들어가 주소가 그 경로로 유지되지 않는다(뒤에 인코딩된 URL 이 붙는 형태).
        if (pushWebviewWrappedScreen(resultUrl)) return;
        navigation.push('WebViewScreen', {
          url: resultUrl,
        });
        return;
      }

      if ((await DeviceInfo.getVersion()) === '2.334.0' && isIOS) {
        appendParams(url, 'url', resultUrl);
        openDeeplink(finalizeUrl(url));
        return;
      }
      appendParams(url, 'url', `${webviewPath}`);
      openDeeplink(finalizeUrl(url));
    },
    [isMainRN, navigation, openDeeplink, pushWebviewWrappedScreen],
  );
};

type MoveToWebviewOptions = {
  titleBar?: boolean;
};

const WEBVIEW_PAGE = {
  /** - 장바구니로 이동 */
  Cart: '/cart?from=CARTICON',
  /** - AI 옷입히기 생성 페이지로 이동 */
  CreateOutfitTryOn:
    '/content/fitting-rooms/outfit-try-on/create?conceptId%3D1%26categoryChipKey%3DrecentlyViewed%26entryPoint%3DRECENTLY_VIEWED_GOOD',
  /** - 뷰티관으로 이동  */
  BeautyDepartment: '/screens/?screen_name=BEAUTY_DEPARTMENT',
  /** - 이용약관 */
  TermsOfUse: '/agreement/terms-of-use',
  /** - 개인정보 처리방침 */
  Privacy: '/agreement/privacy',
  /** - 청소년 보호정책 */
  YouthPolicy: '/youth-policy',
} satisfies Record<string, `/${string}`>;
